import * as queryString from "query-string";

import React, { Component } from "react";

import { TimeSpent } from "utils/timeSpent";
import { __Ashby } from "utils/ashby";
import { analytics } from "utils/analytics";
import { compose } from "redux";
import styled from "styled-components";
import theme from "styles/theme";
import withRouter from "hocs/withRouter";

const Application = styled.div``;

const LeverAppContainer = styled.div`
  margin-top: ${theme.spacing.unit * 4}px;
  height: ${(props) =>
    props.applicationFormHeight ? props.applicationFormHeight : 2400}px;
`;

const GreenhouseAppContainer = styled.div`
  ${(props) => {
    if (props.$narrow) {
      return `
        margin: 0 auto;
        width: 660px;
        height: 2400px;
        
        ${theme.media.phone`
          width: 100%;
        `}
      `;
    } else {
      return `
        margin: 0 ${props.$shouldFudgeMargins ? "-10px" : 0};
        
        ${theme.media.phone`
          margin: 0;
        `}
      `;
    }
  }}
`;

const JazzHrAppContainer = styled.div`
  margin: 0 auto;
  max-width: 640px;
  height: ${(props) =>
    props.applicationFormHeight ? props.applicationFormHeight : 800}px;

  > iframe {
    border: none;
    width: 100%;
    height: 100%;
  }

  ${theme.media.phone`
    width: 100%;
  `}
`;

const ManualAppContainer = styled.div`
  margin-top: ${theme.spacing.unit * 4}px;
  width: 100%;
  height: 700px;

  > iframe {
    border: none;
    width: 100%;
    height: 100%;
  }
`;

export class JobPageApplication extends Component {
  greenhouseHeightTimer = null;

  constructor(props) {
    super(props);

    this.appFormRef = React.createRef();
    this.isFillingOutForm = false;
  }

  componentDidMount() {
    const { location, jobPage, analyticsPageName } = this.props;
    const queryParams = queryString.parse(location.search);

    this.timeFillingOutForm = new TimeSpent((data) => {
      analytics.track(`${analyticsPageName} Filling Out Form Time Spent`, {
        jobPage,
        queryParams,
        duration: data.intervalSec,
        totalDuration: data.totalElapsedSec,
      });
    });

    this.initAtsForm(jobPage);
    this.monitorFormActivity();
  }

  componentWillUnmount() {
    if (this.timeFillingOutForm) {
      this.timeFillingOutForm.stop();
    }
    if (this.formActivityInterval) {
      clearInterval(this.formActivityInterval);
    }
    if (this.greenhouseHeightTimer) {
      clearTimeout(this.greenhouseHeightTimer);
    }
  }

  initAtsForm = (jobPage) => {
    if (jobPage.atsSourcePlatform === "greenhouse") {
      this.initGreenhouseForm(jobPage);
    } else if (jobPage.atsSourcePlatform === "lever") {
      this.initLeverForm(jobPage);
    } else if (jobPage.atsSourcePlatform === "ashby") {
      this.initAshbyForm(jobPage);
    } else if (jobPage.atsSourcePlatform === "jazzhr") {
      this.initJazzHrForm(jobPage);
    }
  };

  monitorFormActivity() {
    this.formActivityInterval = setInterval((e) => {
      if (!this.timeFillingOutForm) {
        return;
      }

      const elem = document.activeElement;
      if (elem && elem.tagName.toLowerCase() === "iframe") {
        if (!this.isFillingOutForm) {
          this.timeFillingOutForm.start();
          dispatchEvent(
            new CustomEvent("adminEvent", {
              detail: "Started filling out form",
            })
          );
        }
        this.isFillingOutForm = true;
      } else if (this.isFillingOutForm) {
        this.timeFillingOutForm.stop();
        dispatchEvent(
          new CustomEvent("adminEvent", {
            detail: "Stopped filling out form",
          })
        );
        this.isFillingOutForm = false;
      }
    }, 500);
  }

  checkGreenhouseFormHeight = () => {
    const { location, jobPage, analyticsPageName } = this.props;
    const queryParams = queryString.parse(location.search);

    const formHeight = document.getElementById("grnhse_app").clientHeight;
    if (formHeight < 200) {
      analytics.track(`${analyticsPageName} Greenhouse Form Height Error`, {
        jobPage,
        queryParams,
        formHeight,
      });
    }
  };

  getGreenhouse = async (formType) => {
    if (formType === "greenhouse-iframe-v2") {
      return (await import("utils/greenhouseV2")).Grnhse;
    } else {
      return (await import("utils/greenhouseV1")).Grnhse;
    }
  };

  initGreenhouseForm = async (jobPage) => {
    let params = new URL(window.location).searchParams;
    const boardId = params.get("boardId") || jobPage.jobBoard.atsSourceId;
    const appId = params.get("appId") || jobPage.atsSourceId;

    const Grnhse = await this.getGreenhouse(jobPage.formType);
    if (jobPage.formType === "greenhouse-iframe-v2") {
      Grnhse.Settings.boardURI = `https://job-boards.greenhouse.io/embed/job_board?for=${boardId}`;
      Grnhse.Settings.applicationURI = `https://job-boards.greenhouse.io/embed/job_app?for=${boardId}`;
    } else {
      Grnhse.Settings.boardURI = `https://boards.greenhouse.io/embed/job_board?for=${boardId}`;
      Grnhse.Settings.applicationURI = `https://boards.greenhouse.io/embed/job_app?for=${boardId}`;
    }

    Grnhse.Iframe.load(appId);

    // Check the height of the form in 5s
    this.greenhouseHeightTimer = setTimeout(
      this.checkGreenhouseFormHeight,
      5000
    );
  };

  initLeverForm = (jobPage) => {
    const apiDomain = jobPage.jobBoard.isSandbox
      ? "jobs.sandbox.lever.co"
      : "jobs.lever.co";
    const baseUrl = `https://${apiDomain}/${jobPage.jobBoard.atsSourceId}/${jobPage.atsSourceId}/apply`;
    const iframeUrl = new URL(baseUrl);

    // pass lever tracking params to iframe
    let locationParams = new URL(window.location).searchParams;
    if (locationParams) {
      locationParams.forEach((value, key, parent) => {
        if (key.startsWith("lever-")) {
          iframeUrl.searchParams.set(key, value);
        }
      });
    }

    this.appFormRef.current.insertAdjacentHTML(
      "beforeend",
      `<iframe id="postings-iframe" seamless frameborder="0" allowtransparency="true" src="${iframeUrl.href}" style="width: 100%; height: 100%;" loading="lazy">
    Your browser does not appear to support iframes. See <a href="https://${apiDomain}/example">all job postings</a>.
  </iframe>
  <script type="text/javascript">
  function resizePostings(height) {
    var iframe = document.getElementById('postings-iframe');
    if (iframe) {
      iframe.style.height = height + 'px';
    }
  }
  </script>    
    `
    );
  };

  initAshbyForm = (jobPage) => {
    __Ashby.settings.ashbyBaseJobBoardUrl = `https://jobs.ashbyhq.com/${jobPage.jobBoard.atsSourceId}`;
    __Ashby.settings.autoScroll = false;
    __Ashby.initialize();
  };

  initJazzHrForm = (jobPage) => {};

  render() {
    const { jobPage } = this.props;

    return (
      <Application>
        {jobPage.atsSourcePlatform === "lever" && (
          <LeverAppContainer
            id="lever_app"
            ref={this.appFormRef}
            applicationFormHeight={
              jobPage.employerProfile.applicationFormHeight
            }
          ></LeverAppContainer>
        )}
        {jobPage.atsSourcePlatform === "greenhouse" && (
          <GreenhouseAppContainer
            $narrow={this.props.narrow}
            $shouldFudgeMargins={jobPage.formType !== "greenhouse-iframe-v2"}
          >
            <div id="grnhse_app"></div>
          </GreenhouseAppContainer>
        )}
        {jobPage.atsSourcePlatform === "ashby" && (
          <div
            id="ashby_embed"
            data-jid={jobPage.atsSourceId}
            data-tab="application"
            data-noChrome="true"
          ></div>
        )}
        {jobPage.atsSourcePlatform === "jazzhr" && (
          <JazzHrAppContainer
            applicationFormHeight={
              jobPage.employerProfile.applicationFormHeight
            }
          >
            <iframe
              id="jazzhr_app"
              title="jazzhr"
              src={`https://${jobPage.jobBoard.atsSourceId}.applytojob.com/apply/embed/form/${jobPage.jazzHrBoardCodeId}`}
            />
          </JazzHrAppContainer>
        )}
        {jobPage.atsSourcePlatform === "manual" && (
          <ManualAppContainer>
            <iframe
              id="manual_app"
              title="typeform"
              src={`${jobPage.atsSourceId}?typeform-embed=embed-fullpage`}
            />
          </ManualAppContainer>
        )}
      </Application>
    );
  }
}

const enhance = compose(withRouter);

export default enhance(JobPageApplication);
